import useGoals from '@/composables/goal/goals';
import useProperties from '@/composables/property/property';
import useSpaces from '@/composables/space/spaces';
import useUsers from '@/composables/user/users';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import {
  BETWEEN, EXACT_DATE,
  GT,
  LAST_MONTH,
  LAST_WEEK,
  LAST_X_DAYS,
  LT,
  NEXT_X_DAYS,
  THIS_MONTH,
  THIS_WEEK,
  TODAY,
} from '@/lib/constants';
import { DateTime } from 'luxon';
import { GOAL_RELATION, GOAL_SELF } from '@/lib/props/custom-types';
import { dateScopeDynamicType, dateScopeType, intervalType, propertyType, userScopeType } from 'shared/constants.json';
import { isEmptyRange as isEmptyDateRange } from '@/lib/filter/dynamic-date';
import { isEmptyRange as isEmptyNumberRange } from '@/lib/filter/number';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { numberFormatter } from '@/lib/props/number';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

export default function useScopeItem() {
  const propertiesSvc = useProperties();
  const usersSvc = useUsers();
  const spacesSvc = useSpaces();
  const goalsSvc = useGoals();
  const { t } = useI18n();

  const numberIntervalTypes = [
    { text: '<', value: intervalType.lt },
    { text: '≤', value: intervalType.le },
    { text: '=', value: intervalType.eq },
    { text: '≥', value: intervalType.ge },
    { text: '>', value: intervalType.gt },
  ];
  const dateSingleDynamicTypes = [
    { text: t('dateScope.today'), value: TODAY },
    { text: t('dateScope.xDaysFromNow'), value: NEXT_X_DAYS },
    { text: t('dateScope.xDaysAgo'), value: LAST_X_DAYS },
    { text: t('dateScope.beginningThisWeek'), value: THIS_WEEK },
    { text: t('dateScope.beginningLastWeek'), value: LAST_WEEK },
    { text: t('dateScope.beginningThisMonth'), value: THIS_MONTH },
    { text: t('dateScope.beginningLastMonth'), value: LAST_MONTH },
    { text: t('dateScope.exactDate'), value: EXACT_DATE },
  ];
  const dateRangeDynamicTypes = [
    { text: t('dateScope.today'), value: TODAY },
    { text: t('dateScope.nextXDays'), value: NEXT_X_DAYS },
    { text: t('dateScope.lastXDays'), value: LAST_X_DAYS },
    { text: t('dateScope.thisWeek'), value: THIS_WEEK },
    { text: t('dateScope.lastWeek'), value: LAST_WEEK },
    { text: t('dateScope.thisMonth'), value: THIS_MONTH },
    { text: t('dateScope.lastMonth'), value: LAST_MONTH },
    { text: t('dateScope.dateRange'), value: EXACT_DATE },
  ];
  const dateIntervalTypes = [
    { text: t('dateScope.isBefore'), value: LT, dynamicTypes: dateSingleDynamicTypes },
    { text: t('dateScope.isAfter'), value: GT, dynamicTypes: dateSingleDynamicTypes },
    { text: t('dateScope.isBetween'), value: BETWEEN, dynamicTypes: dateRangeDynamicTypes },
  ];

  const operatorTypes = (propType, { hideEmpty = false } = { hideEmpty: false }) => {
    const elementOperators = [
      { text: t('optionScope.is'), value: AND },
      { text: t('optionScope.not'), value: NOT },
    ];
    const collectionOperators = [
      { text: t('optionScope.contains'), value: AND },
      { text: t('optionScope.notContains'), value: NOT },
    ];
    const emptyOperators = [
      { text: t('optionScope.isEmpty'), value: EMPTY },
      { text: t('optionScope.isNotEmpty'), value: NOT_EMPTY },
    ];

    const operatorTypeMap = {
      [GOAL_RELATION]: elementOperators,
      [GOAL_SELF]: elementOperators,
      [propertyType.lookup]: elementOperators,
      [propertyType.number]: numberIntervalTypes,
      [propertyType.date]: dateIntervalTypes,
    };
    const opTypes = operatorTypeMap[propType] || collectionOperators;

    if (!hideEmpty) {
      return opTypes.concat(emptyOperators);
    }

    return opTypes;
  };

  const selectedScopeType = (scope) => {
    if (scope.type !== undefined) {
      return scope.type;
    }
    if (!isNullOrUndefined(scope.staticUsers)) {
      return userScopeType.staticUsers;
    }
    if (!isNullOrUndefined(scope.directProperty)) {
      return userScopeType.directProperty;
    }
    return userScopeType.property;
  };

  const selectedScopeItem = (scopeItems, scope) => {
    switch (selectedScopeType(scope)) {
      case userScopeType.staticUsers:
        return scopeItems.find((item) => item.type === selectedScopeType(scope));
      case userScopeType.directProperty:
        return scopeItems.find((item) => item.type === selectedScopeType(scope) && item.directProperty.edgeName === scope.directProperty.edgeName);
      case userScopeType.property:
        return scopeItems.find((item) => item.type === selectedScopeType(scope) && item.property.uid === scope.property.uid);
      default:
        throw new Error('unsupported user scope type');
    }
  };

  const selectedScopePropertyType = (scopeItem) => {
    switch (scopeItem.type) {
      case userScopeType.directProperty:
        return scopeItem.directProperty.type;
      case userScopeType.property:
        return scopeItem.property.type;
      case userScopeType.staticUsers:
        return propertyType.user;
      default:
        return undefined;
    }
  };

  const scopeFromScopeItem = (scopeItem) => {
    const scope = { type: scopeItem.type };
    const propertyScope = {
      users: [],
      selectedOptions: [],
      spaces: [],
      numberRange: null,
      timeRange: null,
      relations: [],
      isEmpty: false,
    };

    if ([userScopeType.directProperty, userScopeType.property].includes(scope.type)) {
      const pType = scopeItem.directProperty?.type || scopeItem.property?.type;
      switch (pType) {
        case propertyType.date:
          propertyScope.timeRange = {
            amount: 7,
            dynamicType: LAST_X_DAYS,
            type: BETWEEN,
          };
          break;
        case propertyType.number:
          propertyScope.numberRange = {
            min: null,
            minType: intervalType.eq,
          };
          break;
        default:
          break;
      }
    }

    switch (scope.type) {
      case userScopeType.staticUsers:
        return { ...scope, staticUsers: [] };
      case userScopeType.directProperty:
        return {
          ...scope,
          ...propertyScope,
          directProperty: scopeItem.directProperty,
        };
      case userScopeType.property:
        return {
          ...scope,
          ...propertyScope,
          property: scopeItem.property,
        };
      default:
        return scope;
    }
  };

  const getTimeRangeLabel = (scope) => {
    if (isEmptyDateRange(scope.timeRange)) {
      return '';
    }

    const { type, dynamicType, amount, start, end } = scope.timeRange;

    if (dynamicType) {
      if (type === dateScopeType.between) {
        switch (dynamicType) {
          case dateScopeDynamicType.today:
            return t('dateScope.today');
          case dateScopeDynamicType.thisWeek:
            return t('dateScope.withinThisWeek');
          case dateScopeDynamicType.lastWeek:
            return t('dateScope.withinLastWeek');
          case dateScopeDynamicType.thisMonth:
            return t('dateScope.withinThisMonth');
          case dateScopeDynamicType.lastMonth:
            return t('dateScope.withinLastMonth');
          case dateScopeDynamicType.thisQuarter:
            return t('dateScope.withinThisQuarter');
          case dateScopeDynamicType.lastQuarter:
            return t('dateScope.withinLastQuarter');
          case dateScopeDynamicType.thisYear:
            return t('dateScope.withinThisYear');
          case dateScopeDynamicType.lastYear:
            return t('dateScope.withinLastYear');
          case dateScopeDynamicType.lastXDays:
            return t('dateScope.withinLastNDays', { value: amount });
          case dateScopeDynamicType.nextXDays:
            return t('dateScope.withinNextNDays', { value: amount });
          case dateScopeDynamicType.exactDate:
            return `${DateTime.fromISO(start).toLocaleString(DateTime.DATE_MED)} - ${DateTime.fromISO(end).toLocaleString(DateTime.DATE_MED)}`;
          default:
            return '';
        }
      } else if (type === dateScopeType.lessThan) {
        switch (dynamicType) {
          case dateScopeDynamicType.today:
            return `${t('dateScope.before')} ${t('dateScope.today')}`;
          case dateScopeDynamicType.yesterday:
            return `${t('dateScope.before')} ${t('dateScope.yesterday')}`;
          case dateScopeDynamicType.thisWeek:
            return `${t('dateScope.before')} ${t('dateScope.thisWeek')}`;
          case dateScopeDynamicType.lastWeek:
            return `${t('dateScope.before')} ${t('dateScope.lastWeek')}`;
          case dateScopeDynamicType.thisMonth:
            return `${t('dateScope.before')} ${t('dateScope.thisMonth')}`;
          case dateScopeDynamicType.lastMonth:
            return `${t('dateScope.before')} ${t('dateScope.lastMonth')}`;
          case dateScopeDynamicType.thisQuarter:
            return `${t('dateScope.before')} ${t('dateScope.thisQuarter')}`;
          case dateScopeDynamicType.lastQuarter:
            return `${t('dateScope.before')} ${t('dateScope.lastQuarter')}`;
          case dateScopeDynamicType.thisYear:
            return `${t('dateScope.before')} ${t('dateScope.thisYear')}`;
          case dateScopeDynamicType.lastYear:
            return `${t('dateScope.before')} ${t('dateScope.lastYear')}`;
          case dateScopeDynamicType.lastXDays:
            return `${t('dateScope.before')} ${t('dateScope.lastNDays', { value: amount })}`;
          case dateScopeDynamicType.nextXDays:
            return `${t('dateScope.before')} ${t('dateScope.nextNDays', { value: amount })}`;
          case dateScopeDynamicType.exactDate:
            return `${t('dateScope.before')} ${DateTime.fromISO(end).toLocaleString(DateTime.DATE_MED)}`;
          default:
            return '';
        }
      } else if (type === dateScopeType.greaterThan) {
        switch (dynamicType) {
          case dateScopeDynamicType.today:
            return `${t('dateScope.after')} ${t('dateScope.today')}`;
          case dateScopeDynamicType.yesterday:
            return `${t('dateScope.after')} ${t('dateScope.yesterday')}`;
          case dateScopeDynamicType.thisWeek:
            return `${t('dateScope.after')} ${t('dateScope.thisWeek')}`;
          case dateScopeDynamicType.lastWeek:
            return `${t('dateScope.after')} ${t('dateScope.lastWeek')}`;
          case dateScopeDynamicType.thisMonth:
            return `${t('dateScope.after')} ${t('dateScope.thisMonth')}`;
          case dateScopeDynamicType.lastMonth:
            return `${t('dateScope.after')} ${t('dateScope.lastMonth')}`;
          case dateScopeDynamicType.thisQuarter:
            return `${t('dateScope.after')} ${t('dateScope.thisQuarter')}`;
          case dateScopeDynamicType.lastQuarter:
            return `${t('dateScope.after')} ${t('dateScope.lastQuarter')}`;
          case dateScopeDynamicType.thisYear:
            return `${t('dateScope.after')} ${t('dateScope.thisYear')}`;
          case dateScopeDynamicType.lastYear:
            return `${t('dateScope.after')} ${t('dateScope.lastYear')}`;
          case dateScopeDynamicType.lastXDays:
            return `${t('dateScope.after')} ${t('dateScope.lastNDays', { value: amount })}`;
          case dateScopeDynamicType.nextXDays:
            return `${t('dateScope.after')} ${t('dateScope.nextNDays', { value: amount })}`;
          case dateScopeDynamicType.exactDate:
            return `${t('dateScope.after')} ${DateTime.fromISO(start).toLocaleString(DateTime.DATE_MED)}`;
          default:
            return '';
        }
      } else if (type === dateScopeType.empty) {
        return t('optionScope.isEmpty');
      } else if (type === dateScopeType.notEmpty) {
        return t('optionScope.isNotEmpty');
      }
    }

    return '';
  };

  const getUserLabel = (scope) => {
    const selectedUser = usersSvc.selectMultiple(scope.users.map((e) => e.uid));
    return selectedUser.map((user) => `${user.firstName} ${user.lastName}`).join(', ');
  };

  const getOptionsLabel = (scope, lang) => {
    const options = propertiesSvc.selectSingle(scope.property.uid).options;
    const selectedOptions = options.filter((p) => scope.selectedOptions.some((selected) => selected.uid === p.uid));
    return selectedOptions.map((option) => {
      if (option.label != null) {
        return textByLang(option?.label, lang);
      }
      return '';
    }).join(', ');
  };

  const getSpaceLabel = (scope) => {
    const selectedSpaces = spacesSvc.selectMultiple(scope.spaces.map((e) => e.uid));
    return selectedSpaces.map((space) => `${space.title}`).join(', ');
  };

  const getNumberLabel = (scope, lang) => {
    if (isEmptyNumberRange(scope.numberRange)) {
      return '';
    }

    let formattedNumber = '';
    const numberValue = scope.numberRange?.min ?? scope.numberRange?.max ?? null;
    const numberFormat = scope.property?.numberFormat ?? '';
    formattedNumber = numberFormatter(numberFormat, lang)(numberValue);

    return `${numberIntervalTypes.find((e) => e.value === (scope.numberRange?.minType || scope.numberRange?.maxType))?.text || ''} ${formattedNumber}`;
  };

  const getRelationsLabel = (scope) => {
    const relations = scope.relations || [];
    if (relations.length === 0) {
      return '';
    }
    const selectedGoals = goalsSvc.selectMultiple(relations.map((r) => r.uid));
    return selectedGoals.map((g) => g?.title || t('general.untitled')).join(', ');
  };

  const getLabel = (scopeItems, scope, lang, op) => {
    let label = '';

    if (scope.isEmpty) {
      if (op === AND) {
        return t('optionScope.isEmpty');
      }
      return t('optionScope.isNotEmpty');
    }

    switch (selectedScopePropertyType(selectedScopeItem(scopeItems, scope))) {
      case propertyType.user:
        label = getUserLabel(scope);
        break;
      case propertyType.space:
        label = getSpaceLabel(scope, lang);
        break;
      case GOAL_RELATION:
      case GOAL_SELF:
        label = getRelationsLabel(scope);
        break;
      case propertyType.date:
        label = getTimeRangeLabel(scope);
        break;
      case propertyType.number:
        label = getNumberLabel(scope, lang);
        break;
      case propertyType.lookup:
        if (scope.property.lookupProperty.type === propertyType.space) {
          label = getSpaceLabel(scope, lang);
        }
        if (scope.property.lookupProperty.type === propertyType.user) {
          label = getUserLabel(scope);
        }
        if ([propertyType.status, propertyType.singleSelect, propertyType.options].includes(scope.property.lookupProperty.type)) {
          label = getOptionsLabel(scope, lang);
        }
        break;
      case propertyType.status:
      case propertyType.options:
      case propertyType.singleSelect:
        label = getOptionsLabel(scope, lang);
        break;

      default:
        label = '';
        break;
    }

    if (label === '') {
      return '';
    }

    if (op === NOT) {
      label = `${t('optionScope.not')} ${label}`;
    }

    return label;
  };

  return {
    operatorTypes,
    selectedScopeType,
    selectedScopeItem,
    selectedScopePropertyType,
    scopeFromScopeItem,
    getLabel,
  };
}
